import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  AppuntamentoFinestra,
  FINESTRA_PREDEFINITA,
  FinestraOraria,
  OrariFinestra,
  allargaFinestraOraria,
  calcolaFinestraOraria,
} from '../utility_objects/FinestraOraria';
import { getGiorniChiusiLavorativo } from '../utility_objects/GiorniChiusiLavorativo';
import { getGiorniVisibili } from '../utility_objects/PresenzeUtils';

/**
 * Viste con la griglia delle ore, le sole in cui la finestra conta. TimelineDay
 * (vista orizzontale dei giornalieri) ha le ore sull'asse orizzontale e mostra
 * un giorno solo, come il Giorno.
 */
const VISTE_ORARIE: ReadonlySet<string> = new Set([
  'Day',
  '3Days',
  'WorkWeek',
  'Week',
  'TimelineDay',
]);

export interface FinestraOrariaParametri {
  /**
   * Funzione «Solo orario» accesa, cioè Ore «Prenotabili» nel riquadro
   * Visualizzazione (scelta del calendario, nel suo cookie del PC:
   * selectSoloOrario)
   */
  soloOrario: boolean;
  /**
   * Il calendario mostra la riga Ore. Dove il calendario ha il filtro del
   * punto vendita, senza punto vendita la riga non c'è e le ore restano
   * quelle di sempre; il Personale, senza filtro del punto vendita, la mostra
   * sempre
   */
  conPulsante: boolean;
  view: string;
  /** Data selezionata così com'è nello store: Date o stringa */
  selectedDate: Date | string;
  /**
   * Orari del calendario così come arrivano alla griglia (differiti): la banda
   * calendar dello sfondo per giorno, con più bande da unire dove il calendario
   * ne disegna più di una (Personale, una per punto vendita; giornaliero
   * Utente, una per colonna)
   */
  orari: OrariFinestra | undefined;
  /**
   * Lista appuntamenti dell'agenda, senza le festività; nei giornalieri solo
   * quelli delle colonne mostrate
   */
  appuntamenti: readonly AppuntamentoFinestra[];
  /** La lista è quella della data e dei filtri correnti (chiave della richiesta) */
  appuntamentiPronti: boolean;
  /** Gli orari sono quelli della data e dei filtri correnti, già arrivati al calendario */
  sfondoPronto: boolean;
  /**
   * Ore di sempre del calendario: con Ore «Tutte», nei giorni senza orari,
   * nel Mese e in Agenda. 5-24 se non indicate (il giornaliero Utente parte
   * dalle 7)
   */
  predefinita?: Readonly<FinestraOraria>;
}

export interface FinestraOrariaCalendario extends FinestraOraria {
  /**
   * Non c'è ancora una finestra da mostrare e nessuna da tenere: Ore
   * «Prenotabili» e dati non pronti alla prima apertura del calendario. Il
   * calendario si nasconde dietro il loader, invece di mostrare le ore di
   * sempre e poi stringersi.
   */
  inAttesa: boolean;
  /**
   * Ore «Prenotabili» e dati non ancora pronti: la finestra mostrata è quella
   * tenuta (al più allargata agli appuntamenti nuovi), non quella dei dati
   * correnti. Il calendario tiene aperto il loader.
   */
  provvisoria: boolean;
}

/** Ora intera nel formato di startHour ed endHour di Syncfusion: 5 → '05:00', 24 → '24:00'. */
export const formatOraSyncfusion = (ora: number): string =>
  `${String(ora).padStart(2, '0')}:00`;

// Giorni che decidono la finestra: quelli mostrati, non quelli caricati (in 3
// Giorni se ne caricano 4), e nel Lavorativo senza le colonne nascoste, con la
// stessa regola di useWorkWeekColumns (getGiorniChiusiLavorativo, sugli stessi
// orari): un appuntamento in una colonna larga 0px non si vede e non deve
// allargare la finestra
const getGiorniFinestra = (
  view: string,
  giorno: string,
  orari: FinestraOrariaParametri['orari']
): string[] => {
  const giorni = getGiorniVisibili(view, giorno);
  if (view !== 'WorkWeek') return giorni;
  const chiusi = getGiorniChiusiLavorativo(giorno, orari);
  return giorni.filter((_giorno, indice) => !chiusi[indice]);
};

const stessaFinestra = (a: FinestraOraria, b: FinestraOraria | null): boolean =>
  b !== null && a.startHour === b.startHour && a.endHour === b.endHour;

/**
 * Ore da mostrare in un calendario con la funzione «Solo orario», la riga Ore
 * del riquadro Visualizzazione (agenda negozio, Personale, giornalieri Studio e
 * Utente, anche nella vista Orizzontale). Con «Tutte», senza la riga o fuori
 * dalle viste a griglia oraria, sono le ore di sempre del calendario e non si
 * calcola nulla. Stessa logica di
 * FOCUS CLOUD, con la regola della console per gli appuntamenti a cavallo della
 * mezzanotte ('finoAlle24').
 *
 * La finestra si ricalcola solo con dati pronti, cioè della data e dei filtri
 * correnti: calcolata su dati misti salterebbe (si stringe, poi si riallarga)
 * e lo scroll con lei. Finché non lo sono si tiene quella mostrata prima. Con
 * gli appuntamenti nuovi già arrivati e gli orari ancora quelli di prima, la
 * finestra può solo allargarsi: Syncfusion non disegna gli appuntamenti fuori
 * finestra, e così nessuno resta nascosto in attesa degli orari.
 *
 * Va chiamato da un componente che non si rimonta: lo Scheduler si rimonta a
 * ogni data, vista o filtro, e con la finestra nel suo stato ripartirebbe ogni
 * volta dalle ore di sempre. Tutto si deriva nel render; la finestra tenuta è
 * uno stato aggiornato nel render, non un ref: un render scartato
 * (useDeferredValue ne produce) non deve lasciare una finestra mai mostrata.
 */
export function useFinestraOraria({
  soloOrario,
  conPulsante,
  view,
  selectedDate,
  orari,
  appuntamenti,
  appuntamentiPronti,
  sfondoPronto,
  predefinita = FINESTRA_PREDEFINITA,
}: FinestraOrariaParametri): FinestraOrariaCalendario {
  const vistaOraria = VISTE_ORARIE.has(view);
  const acceso = soloOrario && conPulsante;
  const daCalcolare = vistaOraria && acceso;

  // Le ore di sempre per valore: un oggetto nuovo a ogni render non ricalcola
  const { startHour: inizioPredefinito, endHour: finePredefinita } =
    predefinita;
  const sempre = useMemo(
    (): FinestraOraria => ({
      startHour: inizioPredefinito,
      endHour: finePredefinita,
    }),
    [inizioPredefinito, finePredefinita]
  );

  // L'ultima finestra mostrata in una vista oraria
  const [tenuta, setTenuta] = useState<FinestraOraria | null>(null);

  // La data selezionata può arrivare come Date o come stringa: conta il giorno
  const giorno = dayjs(selectedDate).format('YYYY-MM-DD');
  const giorni = useMemo(
    () => (daCalcolare ? getGiorniFinestra(view, giorno, orari) : []),
    [daCalcolare, view, giorno, orari]
  );

  // Finestra da mostrare adesso; null se va tenuta quella di prima
  const proposta = useMemo((): FinestraOraria | null => {
    if (!vistaOraria) return null;
    if (!acceso) return sempre;
    if (!appuntamentiPronti) return null;
    if (sfondoPronto)
      return calcolaFinestraOraria(
        giorni,
        orari,
        appuntamenti,
        'finoAlle24',
        sempre
      );
    if (tenuta === null) return null;
    return allargaFinestraOraria(tenuta, giorni, appuntamenti, 'finoAlle24');
  }, [
    vistaOraria,
    acceso,
    appuntamentiPronti,
    sfondoPronto,
    giorni,
    orari,
    appuntamenti,
    tenuta,
    sempre,
  ]);

  if (proposta !== null && !stessaFinestra(proposta, tenuta))
    setTenuta(proposta);

  // Mese e Agenda non hanno ore: restituiscono la finestra tenuta, che torna
  // buona rientrando in una vista oraria finché i dati nuovi non sono pronti
  if (!vistaOraria)
    return {
      ...(tenuta ?? sempre),
      inAttesa: false,
      provvisoria: false,
    };

  const provvisoria = acceso && !(appuntamentiPronti && sfondoPronto);
  const mostrata = proposta ?? tenuta;
  return mostrata === null
    ? { ...sempre, inAttesa: true, provvisoria: true }
    : { ...mostrata, inAttesa: false, provvisoria };
}
